<template>
  <!-- Mirrors the L1 category nav (markets/CategoryNavL1.vue): unstyled PrimeVue
       Tabs so there is no theme padding/inset — the first tab sits flush with the
       breadcrumb / title / content. Active tab uses the accent bottom border. -->
  <div class="overflow-x-auto scrollbar-none border-b">
    <Tabs unstyled :value="activeIndex" @update:value="handleTabChange">
      <TabList
        unstyled
        :pt="{
          tablist: { class: ['flex min-w-max', compact ? 'gap-[20px]' : 'gap-[24px]'] },
          content: { class: 'min-w-max' },
        }"
      >
        <template v-for="(tab, index) in tabs" :key="tab.name">
          <button v-if="tab.command" type="button" :class="tabClass(index)" @click="tab.command">
            {{ tab.label }}
            <span
              v-if="tab.newBadge"
              class="ml-1 inline-block px-1.5 py-0.5 text-[10px] font-semibold text-on-accent bg-accent rounded uppercase leading-tight align-middle"
              >{{ $t('newBadge') }}</span
            >
          </button>
          <Tab v-else unstyled :value="index" :class="tabClass(index)">
            {{ tab.label }}
            <span
              v-if="tab.newBadge"
              class="ml-1 inline-block px-1.5 py-0.5 text-[10px] font-semibold text-on-accent bg-accent rounded uppercase leading-tight align-middle"
              >{{ $t('newBadge') }}</span
            >
          </Tab>
        </template>
      </TabList>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';

interface Tab {
  name: string;
  label: string;
  command?: () => void;
  newBadge?: boolean;
}

interface Props {
  tabs: Tab[];
  compact?: boolean;
}

const props = withDefaults(defineProps<Props>(), { compact: false });
const route = useRoute();
const router = useRouter();

// Determine active tab index based on current route name
const activeIndex = computed(() => {
  const currentRouteName = route.name;
  const index = props.tabs.findIndex((tab) => !tab.command && tab.name === currentRouteName);
  return index >= 0 ? index : 0;
});

const handleTabChange = (value: string | number) => {
  const index = typeof value === 'string' ? parseInt(value, 10) : value;
  const selectedTab = props.tabs[index];
  if (selectedTab && !selectedTab.command) {
    router.replace({ name: selectedTab.name, params: route.params, query: route.query });
  }
};

const tabClass = (index: number) => [
  'text-[14px] leading-[18px] whitespace-nowrap border-b-4 transition-colors cursor-pointer',
  props.compact ? 'pt-[4px] pb-[8px]' : 'py-[10px]',
  activeIndex.value === index && !props.tabs[index]?.command
    ? 'border-accent font-semibold'
    : 'border-transparent text-muted-color hover:text-color',
];
</script>

<style scoped>
.scrollbar-none {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.scrollbar-none::-webkit-scrollbar {
  display: none;
}
</style>
<!-- last modified by AI tools on 2026-10-12 -->
